import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getCtaCteEntidad,
  regularizarCtaCteEntidad,
} from "../helpers/rutaReportes";
import { getClientes } from "../helpers/rutaClientes";
import { getProveedores } from "../helpers/rutaProveedores";

/* ----------------------------- utils ----------------------------------- */

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayYYYYMMDD = () => new Date().toISOString().slice(0, 10);

const getUrlParam = (key) => {
  try {
    return new URLSearchParams(window.location.search).get(key) || "";
  } catch {
    return "";
  }
};

const normalizarTipoUrl = (v) =>
  String(v || "").toUpperCase() === "PROVEEDOR" ? "PROVEEDOR" : "CLIENTE";

const firstDayOfYear = (ymd = todayYYYYMMDD()) => {
  const year = String(ymd || todayYYYYMMDD()).slice(0, 4);
  return `${year}-01-01`;
};

const sortES = (a, b) =>
  (a || "").localeCompare(b || "", "es", { sensitivity: "base" });

/** Fecha siempre BA (evita el “me fui a ayer”) */
const fmtFechaBA = (m) => {
  const s = m?.fechaYMD;
  if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, d] = s.split("-");
    return `${d}/${mo}/${y}`;
  }

  if (!m?.fecha) return "";
  try {
    const dt = new Date(m.fecha);
    return new Intl.DateTimeFormat("es-AR", {
      timeZone: "America/Argentina/Buenos_Aires",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(dt);
  } catch {
    const dt = new Date(m.fecha);
    return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("es-AR");
  }
};

const labelKind = (k) => {
  switch ((k || "").toUpperCase()) {
    case "REMITO":
      return "Remito";
    case "COBRO":
      return "Cobranza";
    case "PAGO":
      return "Pago";
    default:
      return k || "";
  }
};

/** limpia false/true/null/undefined */
const cleanStr = (v) => {
  if (v === false || v === true) return "";
  const s = String(v ?? "").trim();
  if (!s) return "";
  const sl = s.toLowerCase();
  if (
    sl === "false" ||
    sl === "true" ||
    sl === "null" ||
    sl === "undefined"
  )
    return "";
  return s;
};

/** Concepto “pro”: agrega comprobante cuando hace falta */
const conceptoUI = (m) => {
  const k = (m?.kind || "").toUpperCase();
  const comp = cleanStr(m?.comprobante);

  let c = cleanStr(m?.concepto);

  // Presentación de cuenta corriente
  c = c
    .replace(/Cobranza\s*\/\s*Cancelación/gi, "Cobranza")
    .replace(/Pago\s*\/\s*Cancelación/gi, "Pago");

  if (!c) return `${labelKind(k)}${comp ? ` ${comp}` : ""}`.trim();

  // saldo inicial no se toca
  if (/saldo\s+inicial/i.test(c)) return c;

  if (k === "REMITO") {
    // si es genérico y no tiene nro dentro, se lo agrego
    const isGen = /^remito\b/i.test(c) && !/\d/.test(c) && comp;
    if (isGen) return `Remito ${comp}`;

    // si no incluye comp y lo tengo, lo sumo
    if (comp && !c.includes(comp)) return `${c} (${comp})`;
    return c;
  }

  // pago/cobro: si no trae comp, se lo agrego
  if ((k === "COBRO" || k === "PAGO") && comp && !c.includes(comp)) {
    return `${c} (${comp})`;
  }

  return c;
};

/* ----------------------- análisis de saldo ------------------------------ */

const EPS = 0.01;

const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;

  const s = String(v ?? "").trim();
  if (!s) return 0;

  // Soporta formato argentino: 1.234.567,89
  const normalizado = s
    .replace(/\$/g, "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const n = Number(normalizado);
  return Number.isFinite(n) ? n : 0;
};

const firstNumber = (...vals) => {
  for (const v of vals) {
    const n = toNumber(v);
    if (Math.abs(n) > EPS) return n;
  }
  return 0;
};

const normalizarComprobante = (v) => {
  const s = cleanStr(v);
  if (!s) return "";
  return s.replace(/^#+/, "").trim();
};

const extraerNroRemitoDeTexto = (txt) => {
  const s = cleanStr(txt);
  if (!s) return "";

  const m1 = s.match(/remito\s*#?\s*([0-9]+)/i);
  if (m1?.[1]) return m1[1];

  const m2 = s.match(/#\s*([0-9]+)/i);
  if (m2?.[1]) return m2[1];

  return "";
};

const getDocLabel = (doc) => {
  const nro = normalizarComprobante(doc?.comprobante || doc?.nro || "");
  return nro ? `Remito ${nro}` : doc?.concepto || "Remito";
};

const getAppArray = (m) => {
  const posibles = [
    m?.aplicaciones,
    m?.aplicacionesRemitos,
    m?.remitosAplicados,
    m?.detalleAplicaciones,
    m?.items,
    m?.detalle,
    m?.remitos,
  ];

  for (const arr of posibles) {
    if (Array.isArray(arr) && arr.length > 0) return arr;
  }

  return [];
};

const getAppImporte = (app) => {
  const a = firstNumber(
    app?.montoAplicado,
    app?.montoaplicado,
    app?.importeAplicado,
    app?.importe,
    app?.monto,
    app?.total,
    app?.aplicado
  );

  if (Math.abs(a) > EPS) return a;

  // Caso San Pancracio: A / B / Flete
  return (
    toNumber(app?.aplicadoA) +
    toNumber(app?.aplicadoB) +
    toNumber(app?.aplicadoFlete)
  );
};

const getAppRemitoKeys = (app) => {
  const keys = [];

  const push = (v) => {
    const s = cleanStr(v);
    if (!s) return;
    keys.push(s);
    keys.push(normalizarComprobante(s));
  };

  push(app?.remito);
  push(app?.remitoId);
  push(app?.idRemito);
  push(app?.refId);
  push(app?.origenId);
  push(app?.docId);
  push(app?.comprobante);
  push(app?.nroRemito);
  push(app?.numero);
  push(app?.nro);

  if (app?.remito && typeof app.remito === "object") {
    push(app.remito?._id);
    push(app.remito?.id);
    push(app.remito?.comprobante);
    push(app.remito?.nroRemito);
    push(app.remito?.numero);
    push(app.remito?.nro);
  }

  return [...new Set(keys.filter(Boolean))];
};

const buildAnalisisSaldo = (movs = [], saldoFinal = {}) => {
  const docs = [];
  const aliasMap = new Map();
  const pagosSinDetalle = [];

  const addAlias = (doc, value) => {
    const s = cleanStr(value);
    if (!s) return;

    aliasMap.set(s, doc);
    aliasMap.set(normalizarComprobante(s), doc);
  };

  // 1) Detecto remitos
  movs.forEach((m, idx) => {
    const kind = String(m?.kind || "").toUpperCase();
    if (kind !== "REMITO") return;

    const comp =
      normalizarComprobante(m?.comprobante) ||
      normalizarComprobante(m?.nroRemito) ||
      extraerNroRemitoDeTexto(m?.concepto) ||
      normalizarComprobante(m?.refId) ||
      `SIN-NRO-${idx}`;

    const debe = firstNumber(
      m?.debe,
      m?.total,
      m?.importe,
      m?.monto,
      m?.totalVenta,
      m?.venta
    );

    const doc = {
      key: cleanStr(m?.refId) || comp || `DOC-${idx}`,
      refId: cleanStr(m?.refId),
      comprobante: comp,
      fecha: fmtFechaBA(m),
      concepto: conceptoUI(m),
      debe,
      aplicado: 0,
      aplicaciones: [],
    };

    docs.push(doc);

    addAlias(doc, doc.key);
    addAlias(doc, doc.refId);
    addAlias(doc, doc.comprobante);
    addAlias(doc, `#${doc.comprobante}`);
  });

  // 2) Aplico cobros/pagos si vienen con detalle de aplicaciones
  movs.forEach((m) => {
    const kind = String(m?.kind || "").toUpperCase();
    if (kind !== "COBRO" && kind !== "PAGO") return;

    const apps = getAppArray(m);
    const haber = toNumber(m?.haber);

    if (!apps.length) {
      if (haber > EPS) {
        pagosSinDetalle.push({
          fecha: fmtFechaBA(m),
          concepto: conceptoUI(m),
          importe: haber,
        });
      }
      return;
    }

    apps.forEach((app) => {
      const importe = getAppImporte(app);
      if (importe <= EPS) return;

      const keys = getAppRemitoKeys(app);
      const doc = keys.map((k) => aliasMap.get(k)).find(Boolean);

      if (!doc) {
        pagosSinDetalle.push({
          fecha: fmtFechaBA(m),
          concepto: conceptoUI(m),
          importe,
        });
        return;
      }

      doc.aplicado += importe;
      doc.aplicaciones.push({
        fecha: fmtFechaBA(m),
        concepto: conceptoUI(m),
        importe,
      });
    });
  });

  const tieneDetalleAplicaciones = docs.some((d) => d.aplicaciones.length > 0);

  const remitos = docs.map((d) => ({
    ...d,
    saldo: d.debe - d.aplicado,
  }));

  const pendientes = tieneDetalleAplicaciones
    ? remitos.filter((d) => d.saldo > EPS)
    : [];

  const aFavor = tieneDetalleAplicaciones
    ? remitos.filter((d) => d.saldo < -EPS)
    : [];

  const totalPendiente = pendientes.reduce(
    (acc, d) => acc + d.saldo,
    0
  );

  const totalAFavor = aFavor.reduce(
    (acc, d) => acc + Math.abs(d.saldo),
    0
  );

  const totalPagosSinDetalle = pagosSinDetalle.reduce(
    (acc, p) => acc + toNumber(p?.importe),
    0
  );

  // Saldo real después de considerar:
  // deuda de remitos
  // - remitos pagados de más
  // - cobranzas recibidas todavía sin imputar
  const saldoNetoCalculado =
    totalPendiente -
    totalAFavor -
    totalPagosSinDetalle;

  // Si estamos hablando de "a cobrar", nunca mostramos negativo.
  const saldoNeto = Math.max(0, saldoNetoCalculado);

  // 3) Compensación sugerida, solo informativa
  const deudas = pendientes.map((d) => ({ ...d, resto: d.saldo }));
  const creditos = aFavor.map((d) => ({ ...d, resto: Math.abs(d.saldo) }));
  const compensaciones = [];

  for (const cred of creditos) {
    for (const deu of deudas) {
      if (cred.resto <= EPS) break;
      if (deu.resto <= EPS) continue;

      const imp = Math.min(cred.resto, deu.resto);

      compensaciones.push({
        origen: getDocLabel(cred),
        destino: getDocLabel(deu),
        importe: imp,
      });

      cred.resto -= imp;
      deu.resto -= imp;
    }
  }

  return {
    remitos,
    pendientes,
    aFavor,
    compensaciones,
    pagosSinDetalle,
    totalPendiente,
    totalAFavor,
    totalPagosSinDetalle,
    saldoNeto,
    saldoNetoCalculado,
    saldoFinalSistema: toNumber(saldoFinal?.saldo),
    tieneDetalleAplicaciones,
  };
};

/* ----------------------------- component -------------------------------- */

export default function ReporteCtaCteEntidad() {
  const tipoUrlInicial = normalizarTipoUrl(getUrlParam("tipo"));
  const idUrlInicial = getUrlParam("id") || getUrlParam("entidadId");
  const hastaUrlInicial = getUrlParam("hasta") || todayYYYYMMDD();
  const desdeUrlInicial = getUrlParam("desde") || firstDayOfYear(hastaUrlInicial);

  const [tipo, setTipo] = useState(tipoUrlInicial); // CLIENTE | PROVEEDOR
  const [desde, setDesde] = useState(desdeUrlInicial);
  const [hasta, setHasta] = useState(hastaUrlInicial);

  const [lista, setLista] = useState([]);
  const [q, setQ] = useState("");
  const [entidadId, setEntidadId] = useState(idUrlInicial);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [showAnalisis, setShowAnalisis] = useState(false);
  const [regularizando, setRegularizando] = useState(false);

  const [autoConsultadoUrl, setAutoConsultadoUrl] = useState(false);

  /* Cargar lista de clientes/proveedores */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setErr("");
        setLista([]);

        const idUrl = getUrlParam("id") || getUrlParam("entidadId");
        const tipoUrl = normalizarTipoUrl(getUrlParam("tipo"));
        const idPreseleccionado = idUrl && tipoUrl === tipo ? idUrl : "";

        setEntidadId(idPreseleccionado);
        setData(null);

        const r =
          tipo === "CLIENTE" ? await getClientes() : await getProveedores();

        const arr = (r?.clientes || r?.proveedores || r || []).map((x) => ({
          id: x?._id || x?.id,
          label: x?.razonsocial || x?.nombre || x?.apellido || "SIN NOMBRE",
        }));

        arr.sort((a, b) => sortES(a.label, b.label));
        if (alive) setLista(arr);
      } catch (e) {
        if (alive) setErr(e?.message || "No se pudo cargar la lista.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [tipo]);

  const listaFiltrada = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    if (!s) return lista;
    return lista.filter((x) => (x.label || "").toLowerCase().includes(s));
  }, [lista, q]);

  const entidadLabel = useMemo(() => {
    const f = lista.find((x) => x.id === entidadId);
    return f?.label || "";
  }, [lista, entidadId]);

  const consultar = async () => {
    setErr("");
    setLoading(true);
    setShowAnalisis(false);

    try {
      if (!entidadId) throw new Error("Elegí un cliente/proveedor.");
      if (!desde || !hasta) throw new Error("Completá desde/hasta.");

      const r = await getCtaCteEntidad({ tipo, id: entidadId, desde, hasta });

      console.log("====== RESPUESTA CTA CTE ======");
      console.log(r);
      console.log("VERSION BACKEND:", r?.versionReporteCtaCte);
      console.log("ANALISIS REMITOS:", r?.analisisRemitos);
      console.log("===============================");
      
      if (!r?.ok)
        throw new Error(r?.error || "No se pudo cargar la cuenta corriente.");

      setData(r);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoConsultadoUrl) return;

    const idUrl = getUrlParam("id") || getUrlParam("entidadId");
    const tipoUrl = normalizarTipoUrl(getUrlParam("tipo"));

    if (!idUrl || tipoUrl !== tipo) return;
    if (!lista.length) return;

    const existe = lista.some((x) => String(x.id) === String(idUrl));
    if (!existe) return;

    if (String(entidadId) !== String(idUrl)) {
      setEntidadId(idUrl);
      return;
    }

    setAutoConsultadoUrl(true);
    consultar();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConsultadoUrl, lista, tipo, entidadId]);

  const tot = data?.totalesPeriodo || {};
  const s0 = data?.saldoInicial || {};
  const sf = data?.saldoFinal || {};
  const movs = data?.movimientos || [];

  const analisisSaldo = useMemo(() => {
    if (!data) return null;

    const ar = data?.analisisRemitos;

    // NUEVO: usamos el análisis ya armado por backend
    if (ar) {
      const pendientes = Array.isArray(ar.pendientes)
        ? ar.pendientes
        : [];

      const aFavor = Array.isArray(ar.aFavor)
        ? ar.aFavor
        : [];

      const compensaciones = Array.isArray(ar.compensaciones)
        ? ar.compensaciones
        : [];

      const pagosSinDetalle = Array.isArray(ar.pagosSinRemito)
        ? ar.pagosSinRemito
        : [];

      const totalPendiente = toNumber(ar.totalPendiente);
      const totalAFavor = toNumber(ar.totalAFavor);

      const totalPagosSinDetalle = pagosSinDetalle.reduce(
        (acc, p) => acc + toNumber(p?.importe),
        0
      );

      const saldoNetoCalculado =
        totalPendiente -
        totalAFavor -
        totalPagosSinDetalle;

      const saldoNeto = Math.max(0, saldoNetoCalculado);

      return {
        origenBackend: true,
        tieneDetalleAplicaciones: true,

        pendientes,
        aFavor,
        compensaciones,
        pagosSinDetalle,

        totalPendiente,
        totalAFavor,
        totalPagosSinDetalle,

        saldoNeto,
        saldoNetoCalculado,

        saldoFinalSistema: toNumber(sf?.saldo),
      };
    }

    // RESPALDO: análisis viejo si el backend aún no manda analisisRemitos
    return {
      ...buildAnalisisSaldo(movs, sf),
      origenBackend: false,
    };
  }, [data, movs, sf]);

  const regularizarSaldos = async () => {
    if (!entidadId) {
      alert("No hay cliente/proveedor seleccionado.");
      return;
    }

    if (!analisisSaldo?.origenBackend) {
      alert(
        "La regularización requiere el análisis generado por el backend."
      );
      return;
    }

  const compensaciones =
    Array.isArray(analisisSaldo?.compensaciones)
      ? analisisSaldo.compensaciones
      : [];

  const pagosSinDetalle =
    Array.isArray(analisisSaldo?.pagosSinDetalle)
      ? analisisSaldo.pagosSinDetalle
      : [];

  // Por ahora regularizamos como crédito sin remito
  // únicamente saldos a favor de OC.
  const creditosSinRemito =
    tipo === "CLIENTE"
      ? pagosSinDetalle.filter((p) =>
          String(p?.tipoAplicacion || "")
            .toUpperCase()
            .includes("SALDO_FAVOR")
        )
      : [];

  const totalEntreRemitos = compensaciones.reduce(
    (acc, c) => acc + toNumber(c?.importe),
    0
  );

  const totalCreditosSinRemito = creditosSinRemito.reduce(
    (acc, p) => acc + toNumber(p?.importe),
    0
  );

  const totalSugerido =
    totalEntreRemitos +
    totalCreditosSinRemito;

  if (!(totalSugerido > EPS)) {
    alert(
      "No hay saldos opuestos ni créditos sin imputar para regularizar."
    );
    return;
  }

  const entidadTipoTexto =
    tipo === "CLIENTE"
      ? "cliente"
      : "proveedor";

  const confirmar = window.confirm(
    `Se van a regularizar los saldos de ${entidadLabel || entidadTipoTexto}.\n\n` +
      `Entre remitos: $ ${fmtARS(totalEntreRemitos)}\n` +
      `Créditos sin remito: $ ${fmtARS(totalCreditosSinRemito)}\n` +
      `Total a regularizar: $ ${fmtARS(totalSugerido)}\n\n` +
      `Esta operación NO genera una OC/OP nueva y NO modifica Caja, Banco ni Cartera.\n` +
      `Solamente redistribuye cobros entre remitos y aplica saldos a favor existentes.\n\n` +
      `¿Deseás continuar?`
  );

  if (!confirmar) {
    return;
  }

  setRegularizando(true);
  setErr("");

  try {
    const resp =
      await regularizarCtaCteEntidad({
        tipo,
        id: entidadId,

        observaciones:
          `Regularización inicial de saldos - ${
            entidadLabel ||
            entidadTipoTexto
          }`,
      });

    if (!resp?.ok) {
      throw new Error(
        resp?.error ||
          "No se pudo realizar la regularización."
      );
    }

    if (
      Number(resp?.aplicadas || 0) === 0
    ) {
      alert(
        resp?.msg ||
          "No había saldos para regularizar."
      );

      return;
    }

    // Volvemos a consultar SIN cerrar el modal.
    const actualizado =
      await getCtaCteEntidad({
        tipo,
        id: entidadId,
        desde,
        hasta,
      });

    if (!actualizado?.ok) {
      throw new Error(
        actualizado?.error ||
          "La regularización se realizó, pero no se pudo actualizar la cuenta corriente."
      );
    }

    setData(actualizado);

    alert(
      `Regularización realizada correctamente.\n\n` +
        `Compensaciones aplicadas: ${resp.aplicadas}\n` +
        `Total regularizado: $ ${fmtARS(
          resp.totalRegularizado
        )}`
    );
  } catch (e) {
    console.error(
      "[regularizarSaldos]",
      e
    );

    alert(
      e?.message ||
        "No se pudo realizar la regularización."
    );
  } finally {
    setRegularizando(false);
  }
};

  /* ============================= PDF =================================== */

const exportarPDF = async (cuenta = "A") => {
  if (!data) return;

  const cuentaPDF = cuenta === "B" ? "B" : "A";

  const campoDebe = cuentaPDF === "A" ? "debeA" : "debeB";
  const campoHaber = cuentaPDF === "A" ? "haberA" : "haberB";
  const campoSaldo = cuentaPDF === "A" ? "saldoA" : "saldoB";

  const saldoInicialCuenta =
    cuentaPDF === "A"
      ? toNumber(s0?.saldoA)
      : toNumber(s0?.saldoB);

  const debePeriodoCuenta =
    cuentaPDF === "A"
      ? toNumber(tot?.debeA)
      : toNumber(tot?.debeB);

  const haberPeriodoCuenta =
    cuentaPDF === "A"
      ? toNumber(tot?.haberA)
      : toNumber(tot?.haberB);

  const saldoFinalCuenta =
    cuentaPDF === "A"
      ? toNumber(sf?.saldoA)
      : toNumber(sf?.saldoB);

  // Movimientos de la cuenta y entregas con precio cero sin importe A/B.
  // Estas entregas se muestran como referencia en ambos PDF.
  const movsCuenta = movs.filter((m) => {
    const debe = toNumber(m?.[campoDebe]);
    const haber = toNumber(m?.[campoHaber]);

    const esRemitoPrecioCero =
      String(m?.kind || "").toUpperCase() === "REMITO" &&
      !/saldo\s+inicial/i.test(String(m?.concepto || "")) &&
      toNumber(m?.precio) === 0 &&
      toNumber(m?.debeA) === 0 &&
      toNumber(m?.debeB) === 0 &&
      toNumber(m?.haberA) === 0 &&
      toNumber(m?.haberB) === 0;

    return (
      Math.abs(debe) > EPS ||
      Math.abs(haber) > EPS ||
      esRemitoPrecioCero
    );
  });

  // ===================== CONFIG MARCA =====================
  const LOGO_URL = "/SanPancracio.png";
  const EMPRESA = "SAN PANCRACIO SRL";
  const LINEA1 =
    "Avda. Perón 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán";
  const LINEA2 =
    "Tel: 381-3008829  •  contacto@sanpancracio.com";
  const FOOTER_TXT =
    "Documento electrónico generado por el sistema San Pancracio";

  // ===================== LOGO =====================
  const loadImageAsDataURL = async (url) => {
    try {
      const r = await fetch(url, { cache: "no-store" });

      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }

      const blob = await r.blob();
      const mime = blob.type || "";

      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();

        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;

        fr.readAsDataURL(blob);
      });

      return { dataUrl, mime };
    } catch (e) {
      console.warn(
        "[PDF] No pude cargar logo:",
        e?.message || e
      );

      return {
        dataUrl: null,
        mime: "",
      };
    }
  };

  const { dataUrl: logoDataUrl, mime: logoMime } =
    await loadImageAsDataURL(LOGO_URL);

  // ===================== PDF =====================
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const M_LEFT = 10;
  const M_RIGHT = 10;
  const M_TOP = 8;
  const M_BOTTOM = 8;

  const HEADER_H = 28;
  const FOOTER_H = 10;

  const HEADER_SHIFT_Y = 1;
  const REPORT_SHIFT_Y = 6;
  const LINE_SHIFT_Y = 4;

  const HEADER_TEXT_BLOCK_W = 160;
  const HEADER_TEXT_X =
    (pageW - HEADER_TEXT_BLOCK_W) / 2;

  const entidadTipo =
    tipo === "CLIENTE" ? "Cliente" : "Proveedor";

  const title = `Cuenta Corriente (${entidadTipo})`;

  const subtitle =
    `${entidadLabel || "(sin nombre)"} — ` +
    `Período: ${desde} a ${hasta}`;

  // ===================== HEADER =====================
  const drawHeader = () => {
    const y0 =
      M_TOP + 2 + HEADER_SHIFT_Y;

    if (logoDataUrl) {
      const LOGO_W = 22;
      const LOGO_H = 18;

      const format =
        (logoMime || "").includes("jpeg")
          ? "JPEG"
          : "PNG";

      try {
        doc.addImage(
          logoDataUrl,
          format,
          M_LEFT,
          y0,
          LOGO_W,
          LOGO_H
        );
      } catch (e) {
        console.warn(
          "[PDF] Logo inválido:",
          e?.message || e
        );
      }
    }

    const xText = HEADER_TEXT_X;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(EMPRESA, xText, y0 + 5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(LINEA1, xText, y0 + 10);
    doc.text(LINEA2, xText, y0 + 14);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(
      title,
      M_LEFT,
      y0 + 22 + REPORT_SHIFT_Y
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(
      subtitle,
      M_LEFT,
      y0 + 27 + REPORT_SHIFT_Y
    );

    doc.setDrawColor(180);

    doc.line(
      M_LEFT,
      M_TOP + HEADER_H + LINE_SHIFT_Y,
      pageW - M_RIGHT,
      M_TOP + HEADER_H + LINE_SHIFT_Y
    );
  };

  // ===================== FOOTER =====================
  const drawFooter = (
    pageNumber,
    totalPages
  ) => {
    const y = pageH - M_BOTTOM;

    doc.setDrawColor(200);

    doc.line(
      M_LEFT,
      y - FOOTER_H + 2,
      pageW - M_RIGHT,
      y - FOOTER_H + 2
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    doc.text(
      FOOTER_TXT,
      M_LEFT,
      y - 2
    );

    doc.text(
      `Página ${pageNumber} de ${totalPages}`,
      pageW - M_RIGHT,
      y - 2,
      { align: "right" }
    );
  };

  // ===================== RESUMEN =====================
  const drawResumen = (startY) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const x = M_LEFT;
    let y = startY;

    doc.text(
      `Saldo inicial: $ ${fmtARS(saldoInicialCuenta)}`,
      x,
      y
    );

    y += 5;

    doc.text(
      `Debe período: $ ${fmtARS(debePeriodoCuenta)}`,
      x,
      y
    );

    y += 5;

    doc.text(
      `Haber período: $ ${fmtARS(haberPeriodoCuenta)}`,
      x,
      y
    );

    y += 5;

    doc.text(
      `Saldo final: $ ${fmtARS(saldoFinalCuenta)}`,
      x,
      y
);

    return y + 4;
  };

  // ===================== TABLA =====================
  const head = [
    [
      "Fecha",
      "Factura",
      "Chofer",
      "Kg Rec.",
      "Precio",
      "Concepto",
      "Debe",
      "Haber",
      "Saldo",
    ],
  ];

  const body = movsCuenta.map((m) => {
    const debe = toNumber(m?.[campoDebe]);
    const haber = toNumber(m?.[campoHaber]);
    const saldo = toNumber(m?.[campoSaldo]);

    const chofer = cleanStr(m?.choferNombre);
    const kgRec = toNumber(m?.kgRecibido);
    const precio = toNumber(m?.precio);

    const k = String(m?.kind || "").toUpperCase();
    const comp = cleanStr(m?.comprobante);

    const concepto =
      /saldo\s+inicial/i.test(String(m?.concepto || ""))
        ? "SALDO INICIAL"
        : k === "REMITO"
        ? `Remito ${comp || ""}`.trim()
        : conceptoUI(m);

    return [
      fmtFechaBA(m),
      cleanStr(m?.nroFac),
      chofer,
      kgRec ? kgRec.toLocaleString("es-AR") : "",
      precio || (
        String(m?.kind || "").toUpperCase() === "REMITO" &&
        !/saldo\s+inicial/i.test(String(m?.concepto || ""))
      ) ? `$ ${fmtARS(precio)}` : "",
      concepto,
      debe ? `$ ${fmtARS(debe)}` : "",
      haber ? `$ ${fmtARS(haber)}` : "",
      `$ ${fmtARS(saldo)}`,
    ];
  });

  // ===================== RENDER =====================
  drawHeader();

  const startYTabla =
    drawResumen(
      M_TOP +
        HEADER_H +
        LINE_SHIFT_Y +
        8
    );

  autoTable(doc, {
    startY: startYTabla,

    head,
    body,

    styles: {
      fontSize: 7,
      cellPadding: 1.2,
      overflow: "linebreak",
    },

    headStyles: {
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },

    margin: {
      left: M_LEFT,
      right: M_RIGHT,
      top:
        M_TOP +
        HEADER_H +
        LINE_SHIFT_Y +
        6,
      bottom:
        M_BOTTOM + FOOTER_H,
    },

    columnStyles: {
      0: { cellWidth: 18 }, // Fecha
      1: { cellWidth: 24 }, // Factura
      2: { cellWidth: 30 }, // Chofer
      3: { cellWidth: 16, halign: "right" }, // Kg
      4: { cellWidth: 16, halign: "right" }, // Precio
      5: { cellWidth: 60 }, // Concepto
      6: { cellWidth: 26, halign: "right" }, // Debe
      7: { cellWidth: 26, halign: "right" }, // Haber
      8: { cellWidth: 26, halign: "right" }, // Saldo
    },

    didDrawPage: (d) => {
      drawHeader();

      if (d.pageNumber === 1) {
        drawResumen(
          M_TOP +
            HEADER_H +
            LINE_SHIFT_Y +
            8
        );
      }
    },
  });

  const totalPages =
    doc.getNumberOfPages();

  for (
    let p = 1;
    p <= totalPages;
    p++
  ) {
    doc.setPage(p);

    drawFooter(
      p,
      totalPages
    );
  }

  doc.save(
    `ctacte-${cuentaPDF}-${tipo}-${(
      entidadLabel || "entidad"
    ).replaceAll(" ", "_")}-${desde}-${hasta}.pdf`
  );
};

const fmtFechaAnalisis = (x) => {
  if (x?.fechaYMD && /^\d{4}-\d{2}-\d{2}$/.test(x.fechaYMD)) {
    const [y, m, d] = x.fechaYMD.split("-");
    return `${d}/${m}/${y}`;
  }

  return fmtFechaBA(x);
};

const labelRemitoAnalisis = (r) => {
  const nro = normalizarComprobante(r?.remito || r?.remitoLabel || "");
  return nro ? `Remito ${nro}` : "Remito";
};

const renderPagosRemito = (r) => {
  const pagos = Array.isArray(r?.pagos) ? r.pagos : [];

  if (!pagos.length) {
    return (
      <div className="text-muted small mt-1">
        Sin pagos aplicados informados.
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="fw-semibold small mb-1">Pagos aplicados:</div>

      <Table bordered size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>OCR / Comprobante</th>
            <th>Tipo</th>
            <th style={{ textAlign: "right" }}>Importe</th>
          </tr>
        </thead>
        <tbody>
          {pagos.map((p, idx) => (
            <tr key={`pago-${r?.remito}-${idx}`}>
              <td>{fmtFechaAnalisis(p)}</td>
              <td>{p?.comprobante || p?.concepto || ""}</td>
              <td>
                {p?.tipoAplicacion ===
                "REGULARIZACION_ORIGEN" ? (
                  <Badge
                    bg="warning"
                    text="dark"
                  >
                    Regularización salida
                  </Badge>
                ) : p?.tipoAplicacion ===
                  "REGULARIZACION_DESTINO" ? (
                  <Badge bg="success">
                    Regularización entrada
                  </Badge>
                ) : p?.esSaldoFavorGenerado ? (
                  <Badge bg="success">
                    Pago de más
                  </Badge>
                ) : (
                  <Badge bg="primary">
                    Aplicación
                  </Badge>
                )}
              </td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                $ {fmtARS(p?.importe)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};


  /* ============================= UI =================================== */

  return (
    <Container fluid="md" className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column gap-2">
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Tipo</Form.Label>
                  <Form.Select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                  >
                    <option value="CLIENTE">Cliente</option>
                    <option value="PROVEEDOR">Proveedor</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={5}>
                <Form.Group>
                  <Form.Label className="mb-1">
                    {tipo === "CLIENTE" ? "Cliente" : "Proveedor"}
                  </Form.Label>
                  <Form.Select
                    value={entidadId}
                    onChange={(e) => setEntidadId(e.target.value)}
                  >
                    <option value="">— Seleccionar —</option>
                    {listaFiltrada.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="mb-1">Buscar en lista</Form.Label>
                  <Form.Control
                    placeholder="Escribí para filtrar…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={desde}
                    onChange={(e) => setDesde(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={hasta}
                    onChange={(e) => setHasta(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex gap-2 justify-content-md-end">
                <Button onClick={consultar} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" /> Consultando…
                    </>
                  ) : (
                    "Consultar"
                  )}
                </Button>

                <Button
                  variant="outline-primary"
                  onClick={() => setShowAnalisis(true)}
                  disabled={!data || loading}
                >
                  Analizar saldo
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={() => exportarPDF("A")}
                  disabled={!data || loading}
                >
                  PDF Cuenta A
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => exportarPDF("B")}
                  disabled={!data || loading}
                >
                  PDF Cuenta B
                </Button>
              </Col>
            </Row>

            {err ? (
              <Alert className="mt-2 mb-0" variant="danger">
                {err}
              </Alert>
            ) : null}
          </div>
        </Card.Body>
      </Card>

      {!data ? (
        <div className="mt-3 text-muted">
          Elegí entidad + rango y tocá <b>“Consultar”</b>. Prometo que no
          muerde… salvo los saldos 😄
        </div>
      ) : (
        <>
          <Row className="mt-3 g-2">
            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Saldo inicial
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(s0.saldo)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Debe (remitos)
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(tot.debe)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Haber (pagos)
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(tot.haber)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Saldo final
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(sf.saldo)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Movimientos</h5>

              <div className="table-responsive">
                  <Table
                    striped
                    bordered
                    hover
                    size="sm"
                    className="mb-0 tabla-ctacte-compacta"
                    style={{
                      tableLayout: "fixed",
                      width: "100%",
                      fontSize: "10px",
                      lineHeight: "1.15",
                    }}
                  >
                  <thead>
                    <tr>
                      <th style={{ width: "7%" }}>Fecha</th>
                      <th style={{ width: "18%" }}>Concepto</th>

                      <th style={{ width: "12%" }}>Chofer</th>
                      <th style={{ width: "7%", textAlign: "right" }}>Kg Rec.</th>
                      <th style={{ width: "7%", textAlign: "right" }}>Precio</th>

                      <th style={{ width: "10%", textAlign: "right" }}>Debe</th>
                      <th style={{ width: "9%", textAlign: "right" }}>Haber</th>
                      {tipo === "CLIENTE" && (
                        <th style={{ width: "10%", textAlign: "right" }}>
                          Saldo Flete
                        </th>
                      )}

                      <th style={{ width: "10%", textAlign: "right" }}>Saldo A</th>
                      <th style={{ width: "10%", textAlign: "right" }}>Saldo B</th>
                      <th style={{ width: "10%", textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {movs.length === 0 ? (
                      <tr>
                        <td colSpan={tipo === "CLIENTE" ? 11 : 10} className="text-center">
                          Sin movimientos.
                        </td>
                      </tr>
                    ) : (
                      movs.map((m, idx) => {
                        const debe = Number(m?.debe || 0);
                        const haber = Number(m?.haber || 0);

                        const isPagoCobro =
                          m?.kind === "PAGO" || m?.kind === "COBRO";

                        const chofer = cleanStr(
                          m?.choferNombre ||
                            m?.chofer?.nombre ||
                            m?.chofer?.apellido ||
                            (typeof m?.chofer === "string" ? m.chofer : "")
                        );

                        const kgRec = firstNumber(
                          m?.kgRecibido,
                          m?.kgRec,
                          m?.kilosRecibidos,
                          m?.kilos,
                          m?.kg
                        );

                        const precio = firstNumber(
                          m?.precio,
                          m?.precioVenta,
                          m?.precioFinal
                        );

                        return (
                          <tr
                            key={m?.refId || `${m?.kind}-${idx}`}
                            style={
                              isPagoCobro
                                ? { background: "#f7fbff" }
                                : undefined
                            }
                          >
                            <td style={{ whiteSpace: "nowrap" }}>
                              {fmtFechaBA(m)}
                            </td>

                            <td>
                              <div className="fw-semibold">
                                {conceptoUI(m)}
                              </div>
                            </td>

                            <td style={{ whiteSpace: "nowrap" }}>
                              {chofer}
                            </td>

                            <td
                              style={{
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {kgRec ? kgRec.toLocaleString("es-AR") : ""}
                            </td>

                            <td
                              style={{
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {precio || (
                                String(m?.kind || "").toUpperCase() === "REMITO" &&
                                !/saldo\s+inicial/i.test(String(m?.concepto || ""))
                              ) ? `$ ${fmtARS(precio)}` : ""}
                            </td>

                            <td
                              style={{
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {debe ? `$ ${fmtARS(debe)}` : ""}
                            </td>

                            <td
                              style={{
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {haber ? `$ ${fmtARS(haber)}` : ""}
                            </td>

                            {tipo === "CLIENTE" && (
                              <td
                                style={{
                                  textAlign: "right",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                $ {fmtARS(m?.saldoFlete)}
                              </td>
                            )}

                            <td
                              style={{
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              $ {fmtARS(m?.saldoA)}
                            </td>

                            <td
                              style={{
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              $ {fmtARS(m?.saldoB)}
                            </td>

                            <td
                              style={{
                                textAlign: "right",
                                whiteSpace: "nowrap",
                                fontWeight: "600",
                              }}
                            >
                              $ {fmtARS(m?.saldo)}
                            </td>
                           
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}

      <Modal
        show={showAnalisis}
        onHide={() => setShowAnalisis(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Análisis de saldo{" "}
            {entidadLabel ? (
              <span className="text-muted">- {entidadLabel}</span>
            ) : null}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!analisisSaldo ? (
            <Alert variant="warning" className="mb-0">
              Primero consultá una cuenta corriente.
            </Alert>
          ) : (
            <>
              {!analisisSaldo.origenBackend ? (
                <Alert variant="warning">
                  El sistema todavía no recibió el análisis por remito desde el backend.
                  Se muestra un cálculo auxiliar con los movimientos disponibles. Si ya subiste
                  el backend nuevo, reiniciá Evennode y volvé a consultar.
                </Alert>
              ) : null}

              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Remitos con deuda
                      </div>
                      <div className="fw-bold" style={{ fontSize: 20 }}>
                        $ {fmtARS(analisisSaldo.totalPendiente)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Saldo a favor
                      </div>
                      <div
                        className="fw-bold text-success"
                        style={{ fontSize: 20 }}
                      >
                        $ {fmtARS(analisisSaldo.totalAFavor)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Neto sugerido a cobrar
                      </div>
                      <div className="fw-bold" style={{ fontSize: 20 }}>
                        $ {fmtARS(analisisSaldo.saldoNeto)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Saldo final sistema
                      </div>
                      <div className="fw-bold" style={{ fontSize: 20 }}>
                        $ {fmtARS(analisisSaldo.saldoFinalSistema)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* {!analisisSaldo.tieneDetalleAplicaciones ? (
                <>
                  <h6 className="fw-bold mt-2">
                    Movimientos detectados para análisis
                  </h6>

                  <div className="table-responsive mb-3">
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Concepto</th>
                          <th style={{ textAlign: "right" }}>Debe</th>
                          <th style={{ textAlign: "right" }}>Haber</th>
                          <th style={{ textAlign: "right" }}>Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movs.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">
                              No hay movimientos.
                            </td>
                          </tr>
                        ) : (
                          movs.map((m, idx) => (
                            <tr key={`mov-analisis-${idx}`}>
                              <td>{fmtFechaBA(m)}</td>
                              <td>{conceptoUI(m)}</td>
                              <td style={{ textAlign: "right" }}>
                                {toNumber(m?.debe)
                                  ? `$ ${fmtARS(m?.debe)}`
                                  : ""}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {toNumber(m?.haber)
                                  ? `$ ${fmtARS(m?.haber)}`
                                  : ""}
                              </td>
                              <td
                                className="fw-bold"
                                style={{ textAlign: "right" }}
                              >
                                $ {fmtARS(m?.saldo)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <>
                  <h6 className="fw-bold mt-2">
                    Remitos con saldo pendiente{" "}
                    <Badge bg="danger">
                      {analisisSaldo.pendientes.length}
                    </Badge>
                  </h6>

                  <div className="table-responsive mb-3">
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Remito</th>
                          <th style={{ textAlign: "right" }}>Venta</th>
                          <th style={{ textAlign: "right" }}>Aplicado</th>
                          <th style={{ textAlign: "right" }}>
                            Saldo pendiente
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {analisisSaldo.pendientes.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">
                              No hay remitos con deuda pendiente.
                            </td>
                          </tr>
                        ) : (
                          analisisSaldo.pendientes.map((d) => (
                            <tr key={`pend-${d.key}`}>
                              <td>{d.fecha}</td>
                              <td>{getDocLabel(d)}</td>
                              <td style={{ textAlign: "right" }}>
                                $ {fmtARS(d.debe)}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                $ {fmtARS(d.aplicado)}
                              </td>
                              <td
                                className="fw-bold"
                                style={{
                                  textAlign: "right",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                $ {fmtARS(d.saldo)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <h6 className="fw-bold mt-2">
                    Remitos con saldo a favor{" "}
                    <Badge bg="success">{analisisSaldo.aFavor.length}</Badge>
                  </h6>

                  <div className="table-responsive mb-3">
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Remito</th>
                          <th style={{ textAlign: "right" }}>Venta</th>
                          <th style={{ textAlign: "right" }}>Aplicado</th>
                          <th style={{ textAlign: "right" }}>Saldo a favor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analisisSaldo.aFavor.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">
                              No hay remitos con saldo a favor.
                            </td>
                          </tr>
                        ) : (
                          analisisSaldo.aFavor.map((d) => (
                            <tr key={`fav-${d.key}`}>
                              <td>{d.fecha}</td>
                              <td>{getDocLabel(d)}</td>
                              <td style={{ textAlign: "right" }}>
                                $ {fmtARS(d.debe)}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                $ {fmtARS(d.aplicado)}
                              </td>
                              <td
                                className="fw-bold text-success"
                                style={{
                                  textAlign: "right",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                $ {fmtARS(Math.abs(d.saldo))}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <h6 className="fw-bold mt-2">Compensación sugerida</h6>

                  <div className="table-responsive mb-3">
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Saldo a favor origen</th>
                          <th>Aplica contra</th>
                          <th style={{ textAlign: "right" }}>
                            Importe compensado
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {analisisSaldo.compensaciones.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center text-muted">
                              No hay compensaciones sugeridas.
                            </td>
                          </tr>
                        ) : (
                          analisisSaldo.compensaciones.map((c, idx) => (
                            <tr key={`comp-${idx}`}>
                              <td>{c.origen}</td>
                              <td>{c.destino}</td>
                              <td
                                className="fw-bold"
                                style={{
                                  textAlign: "right",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                $ {fmtARS(c.importe)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              )} */}

              <h6 className="fw-bold mt-3">
                1. Remitos con saldo pendiente{" "}
                <Badge bg="danger">{analisisSaldo.pendientes.length}</Badge>
              </h6>

              <div className="table-responsive mb-3">
                <Table bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Remito</th>
                      <th style={{ textAlign: "right" }}>
                        {tipo === "CLIENTE" ? "Venta A" : "Compra A"}
                      </th>

                      <th style={{ textAlign: "right" }}>
                        {tipo === "CLIENTE" ? "Venta B" : "Compra B"}
                      </th>

                      {tipo === "CLIENTE" && (
                        <th style={{ textAlign: "right" }}>
                          Flete
                        </th>
                      )}
                      <th style={{ textAlign: "right" }}>Pagado</th>
                      <th style={{ textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {analisisSaldo.pendientes.length === 0 ? (
                      <tr>
                        <td colSpan={tipo === "CLIENTE" ? 7 : 6} className="text-center text-muted">
                          No hay remitos con deuda pendiente.
                        </td>
                      </tr>
                    ) : (
                      analisisSaldo.pendientes.map((r) => (
                        <React.Fragment key={`pend-${r.remitoId || r.remito}`}>
                          <tr>
                            <td>{fmtFechaAnalisis(r)}</td>
                            <td className="fw-semibold">{labelRemitoAnalisis(r)}</td>
                            <td style={{ textAlign: "right" }}>
                              $ {fmtARS(r.ventaA)}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              $ {fmtARS(r.ventaB)}
                            </td>
                            {tipo === "CLIENTE" && (
                              <td style={{ textAlign: "right" }}>
                                $ {fmtARS(r.ventaFlete)}
                              </td>
                            )}
                            <td style={{ textAlign: "right" }}>
                              $ {fmtARS(r.totalPagado)}
                            </td>
                            <td
                              className="fw-bold text-danger"
                              style={{ textAlign: "right", whiteSpace: "nowrap" }}
                            >
                              $ {fmtARS(r.saldoPendiente || r.saldo)}
                            </td>
                          </tr>

                          <tr>
                            <td colSpan={tipo === "CLIENTE" ? 7 : 6} style={{ background: "#fafafa" }}>
                              {renderPagosRemito(r)}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <h6 className="fw-bold mt-3">
                2. Remitos pagados de más{" "}
                <Badge bg="success">{analisisSaldo.aFavor.length}</Badge>
              </h6>

              <div className="table-responsive mb-3">
                <Table bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Remito</th>
                      <th style={{ textAlign: "right" }}>
                        {tipo === "CLIENTE" ? "Venta A" : "Compra A"}
                      </th>

                      <th style={{ textAlign: "right" }}>
                        {tipo === "CLIENTE" ? "Venta B" : "Compra B"}
                      </th>

                      {tipo === "CLIENTE" && (
                        <th style={{ textAlign: "right" }}>
                          Flete
                        </th>
                      )}

                      <th style={{ textAlign: "right" }}>Pagado</th>

                      <th style={{ textAlign: "right" }}>
                        Saldo a favor
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {analisisSaldo.aFavor.length === 0 ? (
                      <tr>
                        <td colSpan={tipo === "CLIENTE" ? 7 : 6} className="text-center text-muted">
                          No hay remitos pagados de más.
                        </td>
                      </tr>
                    ) : (
                      analisisSaldo.aFavor.map((r) => (
                        <React.Fragment key={`fav-${r.remitoId || r.remito}`}>
                          <tr>
                            <td>{fmtFechaAnalisis(r)}</td>
                            <td className="fw-semibold">{labelRemitoAnalisis(r)}</td>
                            <td style={{ textAlign: "right" }}>
                              $ {fmtARS(r.ventaA)}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              $ {fmtARS(r.ventaB)}
                            </td>
                            {tipo === "CLIENTE" && (
                              <td style={{ textAlign: "right" }}>
                                $ {fmtARS(r.ventaFlete)}
                              </td>
                            )}
                            <td style={{ textAlign: "right" }}>
                              $ {fmtARS(r.totalPagado)}
                            </td>
                            <td
                              className="fw-bold text-success"
                              style={{ textAlign: "right", whiteSpace: "nowrap" }}
                            >
                              $ {fmtARS(r.saldoAFavor || Math.abs(r.saldo))}
                            </td>
                          </tr>

                          <tr>
                            <td colSpan={tipo === "CLIENTE" ? 7 : 6} style={{ background: "#fafafa" }}>
                              {renderPagosRemito(r)}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <h6 className="fw-bold mt-3">3. Compensación sugerida</h6>

              <div className="table-responsive mb-3">
                <Table bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Origen saldo a favor</th>
                      <th>Aplica a remito</th>
                      <th style={{ textAlign: "right" }}>Importe compensado</th>
                    </tr>
                  </thead>

                  <tbody>
                    {analisisSaldo.compensaciones.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center text-muted">
                          No hay compensaciones sugeridas.
                        </td>
                      </tr>
                    ) : (
                      analisisSaldo.compensaciones.map((c, idx) => (
                        <tr key={`comp-${idx}`}>
                          <td>{c.origenLabel || c.origen}</td>
                          <td>{c.destinoLabel || c.destino}</td>
                          <td
                            className="fw-bold"
                            style={{ textAlign: "right", whiteSpace: "nowrap" }}
                          >
                            $ {fmtARS(c.importe)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <h6 className="fw-bold mt-3">4. Resultado final</h6>

              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <div className="text-muted small">Total remitos con deuda</div>
                      <div className="fw-bold text-danger">
                        $ {fmtARS(analisisSaldo.totalPendiente)}
                      </div>
                    </Col>

                    <Col md={4}>
                      <div className="text-muted small">Menos saldo a favor</div>
                      <div className="fw-bold text-success">
                        -$ {fmtARS(analisisSaldo.totalAFavor)}
                      </div>
                    </Col>

                    <Col md={4}>
                      <div className="text-muted small">Saldo neto real a cobrar</div>
                      <div className="fw-bold" style={{ fontSize: 22 }}>
                        $ {fmtARS(analisisSaldo.saldoNeto)}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
</Card>

              {analisisSaldo.pagosSinDetalle.length > 0 ? (
                <>
                  <h6 className="fw-bold mt-2">Pagos sin detalle de aplicación</h6>

                  <div className="table-responsive">
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Concepto</th>
                          <th style={{ textAlign: "right" }}>Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analisisSaldo.pagosSinDetalle.map((p, idx) => (
                          <tr key={`sin-det-${idx}`}>
                            <td>{p.fecha}</td>
                            <td>{p.concepto}</td>
                            <td style={{ textAlign: "right" }}>
                              $ {fmtARS(p.importe)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : null}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="success"
            onClick={regularizarSaldos}
            disabled={
              regularizando ||
              !analisisSaldo?.origenBackend
            }
          >
            {regularizando ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Regularizando...
              </>
            ) : (
              "Regularizar saldos"
            )}
          </Button>

          <Button
            variant="secondary"
            onClick={() =>
              setShowAnalisis(false)
            }
            disabled={regularizando}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}